/**
 * Surviving a browser's own page translation.
 *
 * Chrome's translator (and Edge's, and every extension that does the same job)
 * rewrites the live DOM: it takes each text node, replaces it with a `<font>`
 * element holding the translated string, and sometimes merges or splits
 * neighbouring nodes while doing it. React knows nothing about any of that. Its
 * fibre tree still points at the original text nodes, so the next update calls
 *
 *   parent.removeChild(textNode)   -- the node's parent is now a <font>
 *   parent.insertBefore(a, b)      -- `b` was moved under a <font>
 *
 * and the DOM throws `NotFoundError: Failed to execute 'removeChild' on 'Node':
 * The node to be removed is not a child of this node`. React cannot recover
 * from an error thrown mid-commit, so the whole route unmounts and the router's
 * error boundary shows 「予期しないエラーが発生しました」 with a way back to the
 * dashboard - which is exactly the report: translated, some screens fine,
 * others fall over.
 *
 * Which screens fall over is not random. It is the ones that re-render text
 * next to other nodes - a label that changes between 保存する and 保存中…, a
 * conditional string beside a <strong>, a list that reorders. A static screen
 * translates and is never touched again, so it stays up.
 *
 * The guard: if the node being removed is not actually a child, or the
 * reference node is not actually here, do nothing and return - rather than
 * throw. The frame is already wrong (the translator moved things), and React
 * repairs it on the next render; a thrown exception instead destroys the
 * screen. This is the long-standing workaround for the problem and it is
 * deliberately narrow: it changes behaviour ONLY in the case that currently
 * throws.
 *
 * ponytail: a workaround, not the answer. The answer is an English locale, so
 * nobody has to point a machine translator at a Japanese-only interface. This
 * keeps the application usable in the meantime, and stays harmless afterwards.
 */

/*
 * Structural, rather than the DOM's own `Node`.
 *
 * The unit test reaches this module from the server's TypeScript project,
 * which has no `lib.dom` and would not know what `Node` is. Only two members
 * are needed, and naming them keeps the contract legible from both sides.
 */
interface DomNode {
  readonly parentNode: DomNode | null;
}

/** The two methods React calls during commit that a rewritten DOM can break. */
interface Patchable {
  removeChild<T extends DomNode>(child: T): T;
  insertBefore<T extends DomNode>(node: T, child: DomNode | null): T;
}

/**
 * @param proto normally `Node.prototype`. Passed in so this is testable
 *   without a DOM, and so the patch site is visible rather than implicit.
 * @returns whether it patched. False when already patched — installing twice
 *   would wrap the guard in itself.
 */
export function installTranslationSafety(proto: Patchable): boolean {
  /*
   * Own property, not an inherited one: a prototype that merely INHERITS the
   * flag has not been patched itself, and skipping it would leave its own
   * overrides of these two methods unguarded.
   */
  const marked = proto as Patchable & { __translationSafe?: boolean };
  if (Object.hasOwn(marked, '__translationSafe')) return false;
  marked.__translationSafe = true;

  /*
   * Captured off the prototype and always called with an explicit `this`, which
   * is what a prototype method is. eslint's unbound-method rule is warning
   * about the usual mistake - passing a method around and losing its receiver -
   * and that is exactly not what happens here.
   */
  // eslint-disable-next-line @typescript-eslint/unbound-method
  const removeChild = proto.removeChild;
  proto.removeChild = function <T extends DomNode>(this: DomNode, child: T): T {
    if (child.parentNode !== this) return child;
    return removeChild.call(this, child) as T;
  };

  // eslint-disable-next-line @typescript-eslint/unbound-method
  const insertBefore = proto.insertBefore;
  proto.insertBefore = function <T extends DomNode>(this: DomNode, node: T, child: DomNode | null): T {
    if (child !== null && child.parentNode !== this) {
      // Append instead of failing: the node still belongs on this parent, only
      // the position is unknowable now. React reorders it on the next render.
      return insertBefore.call(this, node, null) as T;
    }
    return insertBefore.call(this, node, child) as T;
  };

  return true;
}
